import React, { useEffect, useState } from 'react';
import { RotateCcwIcon, Volume2Icon, WavesIcon } from 'lucide-react';
import { AnnaAvatar } from '../avatar/AnnaAvatar';
import { CaptionLine } from '../ui/CaptionLine';
import { avatarStates } from '../../data/avatarStates';
import { playSound } from '../../utils/sound';
import { avatarThreads } from '../../utils/dbSelectors';
import { useDb } from '../../hooks/useDb';
import { useSpecSettings } from '../../contexts/SpecSettingsContext';
import type { AvatarState } from '../../types/anna';
export function SpecHero() {
  const heroThreads = useDb(avatarThreads);
  const [state, setState] = useState<AvatarState>('rust');
  const [replay, setReplay] = useState(0);
  const [pulse, setPulse] = useState(0);
  const {
    soundOn
  } = useSpecSettings();
  const spec = avatarStates.find(s => s.id === state) ?? avatarStates[0];
  useEffect(() => {
    if (soundOn && spec.soundId) playSound(spec.soundId);
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  return <section id="top" className="relative overflow-hidden" aria-label="Anna, levende aanwezigheid" data-id="element-1192" data-mp="qlzc217r7chm">
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-[46%] h-[1100px] w-[1100px] -translate-x-1/2 -translate-y-1/2" style={{
      background: 'radial-gradient(closest-side, rgb(var(--accent) / 0.12), rgb(var(--accent) / 0.035) 45%, transparent 75%)'
    }} data-id="element-1193" data-mp="lmw3w7wh8q5f" />
      <div className="relative mx-auto grid max-w-[1440px] items-center gap-12 px-6 pb-20 pt-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.55fr)_minmax(0,1fr)] lg:px-12 lg:pt-16" data-id="element-1194" data-mp="19didi3m4qihj">
        <div className="max-w-md lg:self-start lg:pt-10" data-id="element-1195" data-mp="1j8a7kxgi6n7k">
          <h1 className="font-display text-[64px] font-extralight leading-none text-ink lg:text-[80px]" data-id="element-1196" data-mp="1eas25h1p2q4b">Anna</h1>
          <p className="mt-6 text-[18px] leading-relaxed text-ink-soft" data-id="element-1197" data-mp="3l06lgdq5895i">
            Een zelf-gehoste, proactieve AI die bij je woont en met je meewerkt. Het gevoel van JARVIS, gebouwd als
            echt product: altijd aanwezig, denkt vooruit en bouwt informatie om je heen op terwijl ze praat.
          </p>
          <p className="mt-6 text-[15px] leading-relaxed text-ink-muted" data-id="element-1198" data-mp="1oyv12ehk6f40">
            Kies rechts een toestand. Beweeg je muis voor de diepte. Zet bovenin geluid of beperkte beweging aan.
          </p>
        </div>

        <div className="flex flex-col items-center" data-id="element-1199" data-mp="1y031p37jrs19">
          <div className="aspect-square w-full max-w-[560px]" data-id="element-1200" data-mp="22xuoxjx8ypz4">
            <AnnaAvatar state={state} threads={heroThreads} pulseKey={pulse} data-id="element-1201" data-mp="22vid7oi31bui" />
          </div>
          <div className="mt-2 flex min-h-[140px] w-full max-w-[640px] flex-col items-center text-center" data-id="element-1202" data-mp="27uv0ubgs2my5">
            <CaptionLine key={`${state}-${replay}`} text={spec.caption} data-id="element-1203" data-mp="2cpin03ob759k" />
          </div>
          <button type="button" onClick={() => setReplay(r => r + 1)} className="mt-1 inline-flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-1.5 text-[13px] text-ink-muted transition-colors duration-150 hover:text-ink" data-id="element-1204" data-mp="2go7wvma524og">
            <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden data-id="element-1205" data-mp="30sp1wi864w4d" /> Ondertitel opnieuw
          </button>
        </div>

        <div className="lg:self-start lg:pt-10" data-id="element-1206" data-mp="2vqpyx9gcc17y">
          <p id="state-picker-label" className="text-[13px] font-medium text-ink-muted" data-id="element-1207" data-mp="2qs68owups39s">Toestand</p>
          <div role="radiogroup" aria-labelledby="state-picker-label" className="mt-3 grid grid-cols-2 gap-1 sm:grid-cols-3 lg:grid-cols-1" data-id="element-1208" data-mp="2ms4h5gip0q88">
            {avatarStates.map(s => {
            const active = s.id === state;
            return <button key={s.id} type="button" role="radio" aria-checked={active} onClick={() => setState(s.id)} className={`flex items-center gap-3 whitespace-nowrap rounded-xl px-3 py-2.5 text-left text-[15px] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${active ? 'bg-white/[0.07] text-ink' : 'text-ink-muted hover:bg-white/[0.03] hover:text-ink-soft'}`} data-id="element-1209" data-mp="3eklok6r2idtd">
                  <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${active ? 'bg-accent' : 'bg-white/20'}`} aria-hidden data-id="element-1210" data-mp="3mzrhzyr2j6tj" />
                  <span className="truncate" data-id="element-1211" data-mp="3d0ae9mvdn307">{s.label}</span>
                </button>;
          })}
          </div>
          <div className="mt-6 border-t border-white/[0.07] pt-5" data-id="element-1212" data-mp="4k75tewfxaat">
            <p className="text-[15px] leading-relaxed text-ink-soft" data-id="element-1213" data-mp="9jk18o1dnpp0">{spec.motion}</p>
            <p className="mt-3 flex items-center gap-2 text-[13px] text-ink-muted" data-id="element-1214" data-mp="9lecfamuk3fw">
              <Volume2Icon className="h-3.5 w-3.5" aria-hidden data-id="element-1215" data-mp="3rxaw8ynrnjlh" /> {spec.sound}
            </p>
            {state === 'meldt' && <button type="button" onClick={() => {
            setPulse(p => p + 1);
            if (soundOn) playSound('meldt');
          }} className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-white/10 px-3.5 py-2 text-[13px] text-ink-soft transition-colors duration-150 hover:border-white/20 hover:text-ink" data-id="element-1216" data-mp="njcm42q83bp2">
                <WavesIcon className="h-3.5 w-3.5" aria-hidden data-id="element-1217" data-mp="2mp89xto8r90s" /> Golf opnieuw
              </button>}
          </div>
        </div>
      </div>
    </section>;
}